import React, { JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddOperation, Operation } from "rfc6902/diff";
import { read, utils } from "xlsx";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import Input from "@mui/material/Input";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import FeedbackButton from "lib/components/button/FeedbackButton";

import { asTitle } from "utils/Utils";

import { getLoggedInUser } from "authentication/AuthenticationService";

import {
	BlueprintType,
	CreateBlueprintInfo,
	Right,
	UserRights
} from "core/blueprint/base/BlueprintService";
import UserRightsTable from "core/blueprint/base/access/UserAccessTable";
import {
	createCompetenceList,
	updateCompetenceListContent
} from "core/blueprint/competencelist/CompetenceListService";
import { processCompetenceListExcel } from "core/blueprint/competencelist/dialog/Utils";
import {
	createDashbardBlueprint,
	updateDashboardBlueprintContent
} from "core/blueprint/dashboard/DashboardService";
import {
	createReportBlueprint,
	updateReportBlueprintContent
} from "core/blueprint/report/ReportBlueprintService";
import {
	createTrajectoryBlueprint,
	updateTrajectoryBlueprintContent
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import {
	createExtractBlueprint,
	updateExtractBlueprintContent
} from "extractblueprint/ExtractBlueprintService";
import {
	createInformationBlueprint,
	updateInformationBlueprintContent
} from "core/blueprint/information/InformationBlueprintService";

export type CreateBlueprintDialogProps = {
	onClose: () => void;
	onConfirm: (id: number, type: BlueprintType) => void;
};

// TODO: this file is too long.
export default function CreateBlueprintDialog(
	props: Readonly<CreateBlueprintDialogProps>
): JSX.Element {
	const { t } = useTranslation();

	const [type, setType] = useState<BlueprintType>();
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [accesses, setAccesses] = useState<UserRights[]>([
		{
			user: getLoggedInUser(),
			rights: [Right.ADMIN, Right.WRITE, Right.READ]
		}
	]);
	const [accessesValid, setAccessesValid] = useState(true);
	const [patches, setPatches] = useState<Operation[]>([]);

	const reader = new FileReader();
	reader.onload = processFile;

	useEffect(() => {
		const defaultRole: AddOperation = {
			op: "add",
			path: "/roles/0",
			value: {
				name: t("admin"),
				informationRights: {
					"": [true, true, false]
				},
				evaluationRights: [true, true],
				reportRights: {
					"": {
						"": [true, true, true, false]
					}
				},
				adminRights: [true, true, true]
			}
		};

		setPatches([]);
		if (type === BlueprintType.TRAJECTORY) {
			setPatches(previous => previous.concat(defaultRole));
		}
	}, [type]);

	function readFile(event: React.ChangeEvent<HTMLInputElement>): void {
		event.preventDefault();

		if (!event.target.files || event.target.files.length === 0) {
			return;
		}

		const f = event.target.files[0];
		reader.readAsArrayBuffer(f);
	}

	function processFile(evt: ProgressEvent<FileReader>): void {
		if (!evt.target || !evt.target.result) {
			return;
		}

		const bstr = evt.target.result;
		const wb = read(bstr, { type: "array" });
		const ws = wb.Sheets[wb.SheetNames[0]];
		const data = utils.sheet_to_json<string[]>(ws, { header: 1 });
		processData(data);
	}

	function processData(data: string[][]): void {
		switch (type) {
			case BlueprintType.COMPETENCE_LIST:
				setPatches(processCompetenceListExcel(data));
				break;
			default:
				return;
		}
	}

	function typeFromKey(value: string | undefined): BlueprintType {
		return BlueprintType[value as keyof typeof BlueprintType];
	}

	function typeFromValue(value: string): BlueprintType {
		return typeFromKey(
			Object.keys(BlueprintType).find(key => typeFromKey(key) === value)
		);
	}

	// The name can not be empty!
	function nameIsInvalid() {
		return name.trim().length === 0;
	}

	function isValid() {
		return type !== undefined && !nameIsInvalid() && accessesValid;
	}

	function allowsExcelUpload(): boolean {
		return type === BlueprintType.COMPETENCE_LIST;
	}

	function createBlueprint(): Promise<void> {
		const info: CreateBlueprintInfo = {
			name: name,
			description: description,
			userRights: accesses
		};

		let create: (info: CreateBlueprintInfo) => Promise<string>;
		let update: (id: number, patches: Operation[]) => Promise<void>;
		switch (type) {
			case BlueprintType.COMPETENCE_LIST:
				create = createCompetenceList;
				update = updateCompetenceListContent;
				break;
			case BlueprintType.REPORT:
				create = createReportBlueprint;
				update = updateReportBlueprintContent;
				break;
			case BlueprintType.EXTRACT:
				create = createExtractBlueprint;
				update = updateExtractBlueprintContent;
				break;
			case BlueprintType.TRAJECTORY:
				create = createTrajectoryBlueprint;
				update = updateTrajectoryBlueprintContent;
				break;
			case BlueprintType.DASHBOARD:
				create = createDashbardBlueprint;
				update = updateDashboardBlueprintContent;
				break;
			case BlueprintType.INFORMATION:
				create = createInformationBlueprint;
				update = updateInformationBlueprintContent;
				break;
			default:
				console.error("Unsupported blueprint type '" + type + "'");
				return Promise.resolve();
		}

		return create(info)
			.then(location =>
				Number.parseInt(
					location.substring(location.lastIndexOf("/") + 1)
				)
			)
			.then(id => {
				if (patches.length > 0) {
					return update(id, patches).then(() => id);
				} else {
					return id;
				}
			})
			.then(id => props.onConfirm(id, type))
			.catch(); // TODO: error handling.
	}

	return (
		<Dialog
			open={true}
			onClick={e => e.stopPropagation()}
			onClose={props.onClose}>
			<DialogTitle>{asTitle(t("createBlueprint"))}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							id="type"
							label={asTitle(t("type"))}
							select
							required
							fullWidth
							value={type ?? ""}
							onChange={e =>
								setType(typeFromValue(e.target.value))
							}
							variant="standard">
							{Object.values(BlueprintType).map(type => {
								return (
									<MenuItem key={type} value={type}>
										{t(type.toLowerCase())}
									</MenuItem>
								);
							})}
						</TextField>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id="name"
							type="text"
							label={asTitle(t("name"))}
							error={nameIsInvalid()}
							required
							fullWidth
							inputProps={{ maxLength: 255 }}
							helperText={name.length.toString() + "/255"}
							value={name}
							onChange={e => setName(e.target.value)}
							variant="standard"
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id="description"
							type="text"
							label={asTitle(t("description"))}
							fullWidth
							multiline
							rows={3}
							inputProps={{ maxLength: 1000 }}
							helperText={description.length.toString() + "/1000"}
							value={description}
							onChange={e => setDescription(e.target.value)}
							variant="standard"
						/>
					</Grid>
					<Grid item xs={12}>
						<InputLabel id="access">
							{asTitle(t("access"))}
						</InputLabel>
						<UserRightsTable
							accesses={accesses}
							onChange={setAccesses}
							isValid={setAccessesValid}
						/>
					</Grid>
					{allowsExcelUpload() && (
						<Grid item xs={12}>
							<FormControl fullWidth variant="standard">
								<InputLabel shrink={true}>
									{asTitle(t("dataFile"))}
								</InputLabel>
								<Input
									id="data-file"
									type="file"
									inputProps={{ accept: ".xlsx,.xls,.csv" }}
									onChange={readFile}></Input>
							</FormControl>
						</Grid>
					)}
				</Grid>
				<DialogActions>
					<Button variant="outlined" onClick={props.onClose}>
						{asTitle(t("cancel"))}
					</Button>
					<FeedbackButton
						variant="contained"
						label={asTitle(t("create"))}
						disabled={!isValid()}
						onClick={createBlueprint}
					/>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
}
